import { createRouter, createWebHistory } from 'vue-router';
import { routes } from 'vue-router/auto-routes';
import { Role } from '@scala-watch/shared';
import { useAuthStore } from '@/stores/auth';
import { useAccountStore } from '@/stores/account';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    requiresSuperAdmin?: boolean;
    /** Super admins always allowed; account admins need active org + ACCOUNT_ADMIN role. */
    requiresAccountAdminUsers?: boolean;
    /** If set, requires the user's per-account role to be in this list. */
    requiresRole?: Array<'ACCOUNT_ADMIN' | 'ACCOUNT_EDITOR' | 'ACCOUNT_VIEWER'>;
    public?: boolean;
  }
}

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, saved) {
    return saved ?? { top: 0 };
  },
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  const accountSlugFromPath = to.path.match(/^\/account\/([^/]+)/)?.[1];
  const accountStore = useAccountStore();
  const accountIdBeforeSwitch = accountStore.id;

  if (!auth.ready) await auth.refresh();

  const isPublic = to.meta.public === true;
  if (isPublic) return true;

  if (to.meta.requiresAuth !== false && !auth.isAuthenticated) {
    return { path: '/login', query: { redirect: to.fullPath } };
  }

  if (
    accountSlugFromPath === 'default' &&
    auth.isSuperAdmin &&
    auth.isAuthenticated &&
    to.path.endsWith('/settings/branding')
  ) {
    return { path: '/admin/settings/branding', replace: true };
  }
  if (accountSlugFromPath && accountSlugFromPath !== '_' && auth.isAuthenticated) {
    await accountStore.switchBySlug(accountSlugFromPath);
    if (accountStore.id && accountStore.id !== accountIdBeforeSwitch) {
      await auth.refresh();
    }
  }

  if (to.path.startsWith('/admin/') && auth.isSuperAdmin) {
    useAccountStore().enableAllAccountsMode();
  }

  const legacyUsers = to.path.match(/^\/account\/([^/]+)\/users$/);
  if (legacyUsers?.[1] && legacyUsers[1] !== '_') {
    const account = useAccountStore();
    await account.switchBySlug(legacyUsers[1]);
    return { path: '/admin/users', replace: true };
  }

  if (to.meta.requiresSuperAdmin && !auth.isSuperAdmin) {
    return { path: '/' };
  }

  if (to.meta.requiresAccountAdminUsers === true) {
    if (auth.isSuperAdmin) {
      /* continue */
    } else {
      const account = useAccountStore();
      if (!account.id) {
        const firstAdmin = auth.memberships.find((m) => m.role === Role.ACCOUNT_ADMIN);
        if (firstAdmin) {
          await account.setActive(firstAdmin.account);
        }
        if (!account.id) {
          return { path: '/accounts' };
        }
      }
      const membership = auth.memberships.find((m) => m.account.id === account.id);
      if (membership?.role !== Role.ACCOUNT_ADMIN) {
        return { path: '/accounts' };
      }
    }
  }

  const requiredRoles = to.meta.requiresRole;
  if (requiredRoles?.length) {
    const params = to.params as Record<string, string | string[] | undefined>;
    const rawSlug = params.slug;
    const slug = typeof rawSlug === 'string' ? rawSlug : '';
    if (!slug || slug === '_') {
      return { path: '/accounts' };
    }
    if (!auth.isSuperAdmin) {
      const membership = auth.memberships.find((m) => m.account.slug === slug);
      const allowed = membership && requiredRoles.includes(membership.role);
      if (!allowed) {
        return { path: `/account/${slug}/dashboard` };
      }
    }
  }

  return true;
});
